import { reactive, readonly, computed, watch, nextTick } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { getHeadersWithToken } from '@/utils/http'

const form = useForm({
  packageId: null,
  locationId: null,
  subLotId: null,
  sourceSpaceId: null,
  destinationSpaceId: null,
  qty: ''
})

const state = reactive({
  locations: null,
  packageName: null
})

state.selectedLocation = computed(() => {
  return state.locations ? state.locations.find((location) => location.id === form.locationId) : null
})

state.selectedSourceSpace = computed(() => {
  console.log('computing selectedSourceSpace', form.sourceSpaceId)
  return state.selectedLocation ? state.selectedLocation.spaces.find((space) => space.id === form.sourceSpaceId) : null
})

state.selectedDestinationSpace = computed(() => {
  return state.selectedLocation
    ? state.selectedLocation.spaces.find((space) => space.id === form.destinationSpaceId)
    : null
})

state.selectedSubLot = computed(() => {
  return state.selectedLocation
    ? state.selectedLocation.sub_lots.find((subLot) => subLot.sub_lot_num === form.subLotId)
    : null
})

state.sourceSpaces = computed(() => {
  return state.selectedLocation ? state.selectedLocation.spaces : []
})

state.destinationSpaces = computed(() => {
  return state.selectedLocation ? state.selectedLocation.spaces.filter((space) => space.id !== form.sourceSpaceId) : []
})

state.maxMoveQty = computed(() => {
  if (!state.selectedLocation || !state.selectedSubLot) {
    return ''
  }
  return state.selectedSubLot.space_qtys[form.sourceSpaceId]
})

const callbacks = {
  onUpdate: () => {}
}

const setLocations = (data) => {
  state.locations = data
}

const setPackage = ({ id, title }) => {
  form.packageId = id
  state.packageName = title
}

const onUpdate = (cb) => {
  callbacks.onUpdate = cb
}

let stopLocationIdWatcher = () => {}

const watchLocationId = () => {
  return watch(
    () => form.locationId,
    (locationId) => {
      initForm({ locationId })
    }
  )
}

const initForm = ({ locationId = null, spaceId = null, subLotId = null } = {}) => {
  // Stop any active watcher on locationId.
  stopLocationIdWatcher()

  form.reset('locationId', 'subLotId', 'sourceSpaceId', 'destinationSpaceId', 'qty')
  form.clearErrors()
  form.locationId = locationId
  form.subLotId = subLotId
  form.sourceSpaceId = spaceId

  // Watch for locationId changes now that the form is initialized.
  nextTick(() => {
    stopLocationIdWatcher = watchLocationId()
  })
}

watch(
  () => form.sourceSpaceId,
  (newSourceSpaceId) => {
    if (newSourceSpaceId === form.destinationSpaceId) {
      form.destinationSpaceId = null
    }
  }
)

watch(
  () => form.subLotId,
  () => {
    form.qty = null
  }
)

const loadLocationsData = async (packageId, cb) => {
  try {
    const response = await fetch(route('api.tables.package-locations', packageId), {
      method: 'GET',
      headers: getHeadersWithToken()
    })
    const data = await response.json()
    setLocations(data.rows)
    cb(data.rows)
  } catch (error) {
    console.error(error)
  }
}

const moveInventory = async () => {
  if (validateForm() && !form.processing) {
    form.post(route('movements.store'), {
      onSuccess: async () => {
        const response = await fetch(
          route('api.tables.package-locations.show', { package: form.packageId, location: form.locationId }),
          {
            method: 'GET',
            headers: getHeadersWithToken()
          }
        )

        if (!response.ok) {
          throw new Error('Failed to get package location data')
        }

        const data = await response.json()

        callbacks.onUpdate(data.data)
        form.reset('locationId', 'subLotId', 'sourceSpaceId', 'destinationSpaceId', 'qty')
      }
    })
  }
}

const validateForm = () => {
  form.clearErrors()
  ;[
    ['subLotId', 'Sub-lot is required'],
    ['sourceSpaceId', 'Source is required'],
    ['destinationSpaceId', 'Destination is required'],
    ['qty', 'Quantity is required']
  ].forEach(([field, message]) => {
    if (!form[field]) {
      form.setError(field, message)
    }
  })

  if (isNaN(Number(form.qty))) {
    form.setError('qty', 'Invalid quantity')
  } else {
    if (form.qty <= 0) {
      form.setError('qty', 'Invalid quantity')
    }
    if (state.maxMoveQty && form.qty > state.maxMoveQty) {
      form.setError('qty', 'Quantity exceeds available inventory')
    }
  }

  return !form.hasErrors
}

export default {
  state: readonly(state),
  loadLocationsData,
  initForm,
  setPackage,
  onUpdate,
  form,
  validateForm,
  moveInventory
}
